import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ArrowUp, ArrowDown, Trophy, Target } from 'lucide-react';

/**
 * Pure analysis engine: compares single-game stats to all-time stats (ATS).
 */
export function generatePlayerAnalysis({
  two_pt_pct, three_pt_pct, ft_pct, fg_pct, rpg, apg, blk, stl, to,
  two_pt_pct_ats, three_pt_pct_ats, ft_pct_ats, fg_pct_ats, rpg_ats, apg_ats, blk_ats, stl_ats, to_ats
}) {
  const wins = [];
  if (two_pt_pct != null && two_pt_pct_ats != null && two_pt_pct >= two_pt_pct_ats)
    wins.push("You finished at or above your usual level at the rim this game.");
  if (three_pt_pct != null && three_pt_pct_ats != null && three_pt_pct >= three_pt_pct_ats)
    wins.push("Your three-point shooting matched or exceeded your normal performance.");
  if (ft_pct != null && ft_pct_ats != null && ft_pct >= ft_pct_ats)
    wins.push("Your free-throw shooting was as good as or better than usual.");
  if (fg_pct != null && fg_pct_ats != null && fg_pct >= fg_pct_ats)
    wins.push("Your overall shot efficiency matched or beat your average.");
  if (rpg != null && rpg_ats != null && rpg >= rpg_ats)
    wins.push("Your rebounding effort was at least as strong as it normally is.");
  if (apg != null && apg_ats != null && apg >= apg_ats)
    wins.push("You moved the ball and created for teammates at your usual level or better.");
  if (blk != null && blk_ats != null && blk >= blk_ats)
    wins.push("Your rim protection matched or exceeded your typical impact.");
  if (stl != null && stl_ats != null && stl >= stl_ats)
    wins.push("Your defensive activity and steals were at or above your average.");

  const rawOpps = [];
  const apgDrop = apg != null && apg_ats != null && apg < apg_ats;
  const toSpike = to != null && to_ats != null && to > to_ats;
  if (apgDrop && toSpike) {
    rawOpps.push({ key: 'ball_handling', priority: 0, text: "Work on ball handling and decision-making with the ball." });
  } else {
    if (apgDrop) rawOpps.push({ key: 'apg', priority: 1, text: "Work on ball handling and creating for teammates." });
    if (toSpike) rawOpps.push({ key: 'to', priority: 2, text: "Work on ball handling and decision-making with the ball." });
  }
  if (two_pt_pct != null && two_pt_pct_ats != null && two_pt_pct < two_pt_pct_ats)
    rawOpps.push({ key: '2pt', priority: 3, text: "Work on finishing at the rim." });
  if (ft_pct != null && ft_pct_ats != null && ft_pct < ft_pct_ats)
    rawOpps.push({ key: 'ft', priority: 4, text: "Work on free-throw consistency." });
  if (three_pt_pct != null && three_pt_pct_ats != null && three_pt_pct < three_pt_pct_ats)
    rawOpps.push({ key: '3pt', priority: 5, text: "Work on 3-point consistency and 3-point shot selection." });
  if (fg_pct != null && fg_pct_ats != null && fg_pct < fg_pct_ats)
    rawOpps.push({ key: 'fg', priority: 6, text: "Work on overall shot selection and efficiency." });
  if (rpg != null && rpg_ats != null && rpg < rpg_ats)
    rawOpps.push({ key: 'rpg', priority: 7, text: "Work on boxing out and aggressive rebounding." });
  if (stl != null && stl_ats != null && stl < stl_ats)
    rawOpps.push({ key: 'stl', priority: 8, text: "Work on defensive aggression and body positioning." });

  rawOpps.sort((a, b) => a.priority - b.priority);
  const opportunities = rawOpps.slice(0, 3).map(o => o.text);
  const oppKeys = new Set(rawOpps.slice(0, 3).map(o => o.key));

  const tags = [];
  if (oppKeys.has('ball_handling') || oppKeys.has('apg') || oppKeys.has('to'))
    tags.push("Ball handling & decision-making");
  if (oppKeys.has('2pt')) tags.push("Finishing at the rim");
  if (oppKeys.has('ft')) tags.push("Free-throw consistency");
  if (oppKeys.has('3pt')) tags.push("Perimeter shooting");
  else if (oppKeys.has('fg')) tags.push("Shot selection");
  if (oppKeys.has('rpg')) tags.push("Rebounding toughness");
  if (oppKeys.has('stl')) tags.push("Defensive aggression");
  const practice_focus = [...new Set(tags)].slice(0, 3);

  return { wins, opportunities, practice_focus };
}

export function buildAnalysisInputs(game, ats) {
  const v = (x) => (x == null || isNaN(x) ? null : x);
  const pct = (m, a) => (a > 0 ? Math.round((m / a) * 100 * 10) / 10 : null);

  const g = game;
  const gFgm = g.fgm ?? g.stats?.fgm ?? 0;
  const gFga = g.fga ?? g.stats?.fga ?? 0;
  const g3pm = g.three_pm ?? g.stats?.three_pm ?? 0;
  const g3pa = g.three_pa ?? g.stats?.three_pa ?? 0;
  const gFtm = g.ftm ?? g.stats?.ftm ?? 0;
  const gFta = g.fta ?? g.stats?.fta ?? 0;
  const gReb = g.reb ?? g.stats?.reb ?? 0;
  const gAst = g.ast ?? g.stats?.ast ?? 0;
  const gBlk = g.blk ?? g.stats?.blk ?? 0;
  const gStl = g.stl ?? g.stats?.stl ?? 0;
  const gTo = g.to ?? g.stats?.to ?? 0;
  const two_pm = gFgm - g3pm;
  const two_pa = gFga - g3pa;

  const atsAvg = ats.averages || ats;
  const atsTot = ats.totals || {};

  let two_pt_pct_ats;
  if (atsAvg.two_pct != null) {
    two_pt_pct_ats = atsAvg.two_pct;
  } else if (atsTot.fgm != null && atsTot.fga != null) {
    const at2pm = (atsTot.fgm || 0) - (atsTot.three_pm || 0);
    const at2pa = (atsTot.fga || 0) - (atsTot.three_pa || 0);
    two_pt_pct_ats = at2pa > 0 ? Math.round((at2pm / at2pa) * 100 * 10) / 10 : null;
  } else {
    two_pt_pct_ats = null;
  }

  return {
    two_pt_pct: pct(two_pm, two_pa),
    three_pt_pct: pct(g3pm, g3pa),
    ft_pct: pct(gFtm, gFta),
    fg_pct: pct(gFgm, gFga),
    rpg: gReb, apg: gAst, blk: gBlk, stl: gStl, to: gTo,
    two_pt_pct_ats: v(two_pt_pct_ats),
    three_pt_pct_ats: v(atsAvg.three_p_pct ?? atsAvg.three_pct),
    ft_pct_ats: v(atsAvg.ft_pct),
    fg_pct_ats: v(atsAvg.fg_pct),
    rpg_ats: v(atsAvg.rpg ?? atsAvg.reb),
    apg_ats: v(atsAvg.apg ?? atsAvg.ast),
    blk_ats: v(atsAvg.bpg ?? atsAvg.blk),
    stl_ats: v(atsAvg.spg ?? atsAvg.stl),
    to_ats: v(atsAvg.topg ?? atsAvg.to),
    _raw: {
      pts: g.pts ?? g.stats?.pts ?? 0,
      fgm: gFgm, fga: gFga, three_pm: g3pm, three_pa: g3pa,
      ftm: gFtm, fta: gFta, two_pm, two_pa,
      reb: gReb, ast: gAst, stl: gStl, blk: gBlk, to: gTo,
    }
  };
}

function StatCard({ value, label, atsValue, suffix = '', inverse = false }) {
  const display = value ?? '-';
  const atsDisplay = atsValue ?? '-';
  const isNum = typeof value === 'number' && typeof atsValue === 'number';
  const isUp = isNum ? (inverse ? value <= atsValue : value >= atsValue) : null;

  const borderColor = isUp === true ? '#16A085' : isUp === false ? '#C0392B' : '#cbd5e1';
  const bgColor = isUp === true ? 'rgba(22,160,133,0.06)' : isUp === false ? 'rgba(192,57,43,0.06)' : '#f8fafc';
  const valColor = isUp === true ? '#16A085' : isUp === false ? '#C0392B' : '#1e293b';

  return (
    <div
      className="rounded-lg px-2 py-2 sm:p-3 relative"
      style={{ border: `1.5px solid ${borderColor}`, backgroundColor: bgColor }}
      data-testid={`stat-card-${label.toLowerCase().replace(/[^a-z0-9]/g, '')}`}
    >
      {isUp !== null && (
        <span className="absolute top-1 right-1 sm:top-2 sm:right-2">
          {isUp
            ? <ArrowUp className="w-3 h-3 sm:w-3.5 sm:h-3.5" style={{ color: '#16A085' }} />
            : <ArrowDown className="w-3 h-3 sm:w-3.5 sm:h-3.5" style={{ color: '#C0392B' }} />
          }
        </span>
      )}
      <div>
        <span className="text-base sm:text-xl font-bold" style={{ color: valColor }}>
          {display}
        </span>
        <span className="text-[10px] sm:text-xs font-bold text-slate-400 uppercase ml-0.5">{label}</span>
      </div>
      <p className="text-[10px] sm:text-[11px] text-slate-400 mt-0.5">ATS: {atsDisplay}{suffix}</p>
    </div>
  );
}

export default function GameAnalysisModal({ open, onOpenChange, game, ats, opponentName, gameDate }) {
  if (!game || !ats) return null;

  const inputs = buildAnalysisInputs(game, ats);
  const analysis = generatePlayerAnalysis(inputs);
  const r = inputs._raw;

  const atsAvg = ats.averages || ats;
  const ptsAts = atsAvg.ppg ?? atsAvg.pts ?? '-';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto p-4 sm:p-6" data-testid="game-analysis-modal">
        <DialogHeader className="pb-0">
          <DialogTitle className="text-lg sm:text-xl font-bold tracking-tight">
            Game Breakdown
          </DialogTitle>
          <DialogDescription className="text-slate-500 text-sm">
            {opponentName && `vs ${opponentName}`}{gameDate ? ` — ${gameDate}` : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 sm:space-y-5 pt-2">
          <h4 className="text-sm sm:text-base font-bold text-slate-700">Game Performance vs All-Time Stats</h4>

          {/* Row 1: PTS, FG%, 3P%, FT%, 2P% — 3 cols mobile, 5 cols desktop */}
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-1.5 sm:gap-2">
            <StatCard value={r.pts} label="PTS" atsValue={ptsAts} />
            <StatCard value={inputs.fg_pct} label="FG%" atsValue={inputs.fg_pct_ats} suffix="%" />
            <StatCard value={inputs.three_pt_pct} label="3P%" atsValue={inputs.three_pt_pct_ats} suffix="%" />
            <StatCard value={inputs.ft_pct} label="FT%" atsValue={inputs.ft_pct_ats} suffix="%" />
            <StatCard value={inputs.two_pt_pct} label="2P%" atsValue={inputs.two_pt_pct_ats} suffix="%" />
          </div>

          {/* Row 2: REB, AST, STL, BLK, TO — 3 cols mobile, 5 cols desktop */}
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-1.5 sm:gap-2">
            <StatCard value={inputs.rpg} label="REB" atsValue={inputs.rpg_ats} />
            <StatCard value={inputs.apg} label="AST" atsValue={inputs.apg_ats} />
            <StatCard value={inputs.stl} label="STL" atsValue={inputs.stl_ats} />
            <StatCard value={inputs.blk} label="BLK" atsValue={inputs.blk_ats} />
            <StatCard value={inputs.to} label="TO" atsValue={inputs.to_ats} inverse />
          </div>

          {/* Divider */}
          <div className="flex items-center gap-3 py-1">
            <div className="flex-1 border-t border-slate-200" />
            <span className="text-xs sm:text-sm font-medium text-slate-500 bg-slate-100 px-3 py-1 sm:px-4 sm:py-1.5 rounded-full">Game Takeaways</span>
            <div className="flex-1 border-t border-slate-200" />
          </div>

          {/* Wins & Opportunities — stacked on mobile, side-by-side on desktop */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <div data-testid="analysis-wins-section">
              <h3 className="flex items-center gap-1.5 text-sm sm:text-base font-bold mb-2 sm:mb-3" style={{ color: '#16A085' }}>
                <Trophy className="w-4 h-4" />
                Wins
              </h3>
              {analysis.wins.length > 0 ? (
                <ul className="space-y-2 sm:space-y-2.5">
                  {analysis.wins.map((sentence, i) => (
                    <li key={i} className="text-sm sm:text-base text-slate-700 leading-relaxed flex gap-1.5">
                      <span className="text-slate-400 mt-0.5 shrink-0">•</span>
                      <span>{sentence}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-slate-400 italic">No wins identified for this game.</p>
              )}
            </div>

            <div data-testid="analysis-opportunities-section">
              <h3 className="flex items-center gap-1.5 text-sm sm:text-base font-bold mb-2 sm:mb-3" style={{ color: '#C0392B' }}>
                <Target className="w-4 h-4" />
                Opportunities
              </h3>
              {analysis.opportunities.length > 0 ? (
                <ul className="space-y-2 sm:space-y-2.5">
                  {analysis.opportunities.map((sentence, i) => (
                    <li key={i} className="text-sm sm:text-base text-slate-700 leading-relaxed flex gap-1.5">
                      <span className="text-slate-400 mt-0.5 shrink-0">•</span>
                      <span>{sentence}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-slate-400 italic">No opportunities identified.</p>
              )}
            </div>
          </div>

          {/* Practice Focus */}
          {analysis.practice_focus.length > 0 && (
            <div className="flex items-start sm:items-center gap-2 sm:gap-3 flex-wrap" data-testid="analysis-practice-section">
              <span className="text-sm sm:text-base font-bold text-slate-700">Practice Focus</span>
              {analysis.practice_focus.map((tag, i) => (
                <span key={i} className="px-3 py-1 sm:px-3.5 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium bg-slate-100 text-slate-600 border border-slate-200">
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
